import { ExploreOccupant, ExploreRoom, RankedRoom } from '../services/api/exploreService';
import { buildByline, buildHeadline, buildRelevanceLine } from '../utils/roomStory';
import './RoomStoryCard.css';

/**
 * The one room-card component for the news-front-page layout (V2 Amethyst
 * WU-03) — replaces the bespoke `.room-card` JSX in RoomsDashboard and the
 * `.explore-live-card` JSX in ExplorePage. Same anatomy at every size
 * (rooms-screen-news-layout.md §3); only sizing/density changes per variant.
 *
 * Peek-first by design: the card body opens the peek sheet (design §3 —
 * "Join is the sheet's single primary action"). The Hero variant additionally
 * surfaces a direct Join CTA (its own front-page "lead story" privilege, per
 * §2's section table), skipping the peek for the one room the page is
 * confident about.
 */

const CATEGORY_GLYPH: Record<string, string> = {
  games: '🎮',
  lobbies: '🛋️',
  interests: '🎯',
  dating: '💘',
};

function glyphFor(category?: string | null): string {
  return CATEGORY_GLYPH[(category ?? 'lobbies').toLowerCase()] ?? '🌐';
}

function initialOf(name: string): string {
  return (name?.trim()?.charAt(0) || '?').toUpperCase();
}

export type RoomStoryVariant = 'hero' | 'medium' | 'row';

interface RoomStoryCardProps {
  variant: RoomStoryVariant;
  room: ExploreRoom | RankedRoom;
  /** Classroom skin mapping (school-lessons.md §7): teacher + class size, no student photos. */
  isClassroom?: boolean;
  /** Resolved from the live SignalR room (ExploreRoom carries no host field) — the "teacher" in classroom skin. */
  hostName?: string | null;
  /** "For you" empty-room honesty copy (design §4) — omitted everywhere else. */
  invitation?: string | null;
  onPeek: () => void;
  /** Hero-only direct Join CTA. */
  onJoin?: () => void;
  joining?: boolean;
}

export function RoomStoryCard({
  variant,
  room,
  isClassroom = false,
  hostName,
  invitation,
  onPeek,
  onJoin,
  joining = false,
}: RoomStoryCardProps) {
  const headline = buildHeadline(room, { isClassroom });
  const byline = buildByline(room);
  const relevance = isClassroom ? null : buildRelevanceLine(room);
  const isLive = room.humanCount > 0;

  return (
    <article className={`room-story-card variant-${variant}${isClassroom ? ' classroom' : ''}`}>
      <button type="button" className="room-story-body" onClick={onPeek} aria-label={`Peek at ${room.name}`}>
        <div className="room-story-main">
          <h3 className="room-story-headline">
            {isLive && <span className="room-story-live-dot" aria-hidden="true" />}
            {headline}
          </h3>

          {isClassroom ? (
            <div className="room-story-people classroom">
              <span className="room-story-teacher-chip" aria-hidden="true">{initialOf(hostName || 'T')}</span>
              <span className="room-story-people-text">
                {hostName ? `${hostName} · ` : ''}Class of {room.humanCount}
              </span>
            </div>
          ) : (
            <PeopleRow occupants={room.occupants} />
          )}

          {relevance && <p className="room-story-relevance">{relevance}</p>}
          {invitation && <p className="room-story-invitation">{invitation}</p>}

          <p className="room-story-byline">{byline}</p>
        </div>

        <Thumb room={room} />
      </button>

      {variant === 'hero' && onJoin && (
        <div className="room-story-hero-actions">
          <button
            type="button"
            className="room-story-join-cta"
            onClick={onJoin}
            disabled={joining || (room.maxPlayers > 0 && room.humanCount >= room.maxPlayers)}
          >
            {joining
              ? 'Joining…'
              : room.maxPlayers > 0 && room.humanCount >= room.maxPlayers
                ? 'Full'
                : room.requiresApproval
                  ? '🚪 Request to Join'
                  : room.hasPassword
                    ? '🔒 Join'
                    : 'Join'}
          </button>
        </div>
      )}
    </article>
  );
}

function Thumb({ room }: { room: ExploreRoom }) {
  return (
    <div className="room-story-thumb" data-cat={(room.category ?? 'lobbies').toLowerCase()}>
      {room.thumbnailUrl ? (
        <img className="room-story-thumb-img" src={room.thumbnailUrl} alt="" loading="lazy" />
      ) : (
        <span className="room-story-thumb-glyph" aria-hidden="true">{glyphFor(room.category)}</span>
      )}
    </div>
  );
}

function PeopleRow({ occupants }: { occupants: ExploreOccupant[] }) {
  if (!occupants || occupants.length === 0) {
    return <p className="room-story-people-empty">Nobody's here yet.</p>;
  }
  const visible = occupants.slice(0, 5);
  const overflow = occupants.length - visible.length;
  const names = visible.map((o) => o.displayName.split(/\s+/)[0]).join(', ');

  return (
    <div className="room-story-people">
      <div className="room-story-chips">
        {visible.map((o, i) => (
          <span
            key={i}
            className={`room-story-chip ${o.isNpc ? 'npc' : ''}`}
            title={o.isNpc ? `${o.displayName} (AI)` : o.displayName}
          >
            {o.photoUrl ? (
              <img src={o.photoUrl} alt="" className="room-story-chip-photo" />
            ) : (
              <span className="room-story-chip-initial">{initialOf(o.displayName)}</span>
            )}
            {o.isNpc && <span className="room-story-ai-badge">AI</span>}
          </span>
        ))}
      </div>
      <span className="room-story-names">
        {names}
        {overflow > 0 ? ` +${overflow}` : ''}
      </span>
    </div>
  );
}
